/* eslint-disable no-unused-expressions */
import first from 'lodash/first';
import { useEffect } from 'react';
import { Channels, Environments } from './constants';
import HubspotService from '../services/hubspot-service';

function getPrimaryEmailAddress(user) {
  const primaryEmail = user?.emails && first(user.emails);
  if (!primaryEmail) return '';
  if (typeof primaryEmail === 'string') return primaryEmail;
  if (typeof primaryEmail.address === 'string') return primaryEmail.address;
  return '';
}

function getHubspotBaseSettings() {
  return { loadImmediately: !!!localStorage.getItem('user') };
}

function setHubspotConversationIdentification(email, token) {
  window.hsConversationsSettings = {
    ...getHubspotBaseSettings(),
    identificationEmail: email,
    identificationToken: token,
  };
}

function clearStoredHubspotIdentity() {
  sessionStorage.removeItem('hsEmail');
  sessionStorage.removeItem('hsToken');
}

function resetHubspotConversationIdentity() {
  clearStoredHubspotIdentity();
  setHubspotConversationIdentification(undefined, undefined);
}

function loadHubspotWidget() {
  if (window.HubSpotConversations?.widget?.load) {
    window.HubSpotConversations.widget.load();
  }
}

function initHubspot() {
  setHubspotConversationIdentification(undefined, undefined);

  let script = document.createElement('script');
  script.src = `//js.hs-scripts.com/7233458.js`;
  script.async = true;
  script.defer = true;
  document.body.appendChild(script);
}

async function configureHubspot(user) {
  window.HubSpotConversations?.widget?.remove?.();

  const userId = (user?.userId || '').trim();
  const normalizedUserEmail = getPrimaryEmailAddress(user).trim().toLowerCase();

  if (!userId || !normalizedUserEmail) {
    resetHubspotConversationIdentity();
    loadHubspotWidget();
    return;
  }

  const storedEmail = (sessionStorage.getItem('hsEmail') || '').trim().toLowerCase();
  let hsEmail = storedEmail || normalizedUserEmail;
  let hsToken = (sessionStorage.getItem('hsToken') || '').trim();

  if (storedEmail && storedEmail !== normalizedUserEmail) {
    clearStoredHubspotIdentity();
    hsEmail = normalizedUserEmail;
    hsToken = '';
  }

  if (!hsToken) {
    const data = await HubspotService.getVisitorToken({
      id: user.userId,
      firstName: user.firstName,
      lastName: user.lastName,
      email: normalizedUserEmail,
    });

    hsToken = data?.token ? `${data.token}` : '';

    if (hsToken) {
      sessionStorage.setItem('hsEmail', hsEmail);
      sessionStorage.setItem('hsToken', hsToken);
    }
  }

  if (!hsEmail || !hsToken) {
    resetHubspotConversationIdentity();
    loadHubspotWidget();
    return;
  }

  setHubspotConversationIdentification(`${hsEmail}`, `${hsToken}`);
  loadHubspotWidget();
}

function initPendo() {
  (function(apiKey) {
    (function(p, e, n, d, o) {
      var v, w, x, y, z;
      o = p[d] = p[d] || {};
      o._q = o._q || [];
      v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'trackAgent'];
      for (w = 0, x = v.length; w < x; ++w)
        (function(m) {
          o[m] =
            o[m] ||
            function() {
              o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
            };
        })(v[w]);
      y = e.createElement(n);
      y.async = !0;
      y.src = 'https://content.product.chaserhq.com/agent/static/' + apiKey + '/pendo.js';
      z = e.getElementsByTagName(n)[0];
      z.parentNode.insertBefore(y, z);
    })(window, document, 'script', 'pendo');
  })('15a2fc3d-4803-4bfd-6071-fb8f710f4434');
}

const configurePendo = (user, organisation, subscription) => {
  function subscriptionStatus(isInTrial, isSubscribed, isSuspended, isCancelled) {
    if (isInTrial) return 'trial';
    if (isSubscribed) return 'active';
    if (isSuspended) return 'suspended';
    if (isCancelled) return 'cancelled';
    return 'expired';
  }

  const schedules = organisation.schedules;

  const nylasConnected =
    !!organisation.users && !!organisation.users.find(u => u.nylas?.isConnected);
  const chasers = schedules?.flatMap(s => s.chasers) || [];
  const isMonthlyStatement = !!schedules.find(s => s.monthlyStatement.active);
  const isSMSUser = !!chasers.find(c => c.channel === Channels.SMS);

  window.pendo.initialize({
    visitor: {
      id: user.userId,
      email: user.emails[0].address,
      full_name: `${user.firstName} ${user.lastName}`,
      is_owner: user.isOwner,
      is_subscriber: user.isSubscriber,
      is_partner: user.isPartnerSubscribed,
      joined: user.createdAt,
    },
    account: {
      id: organisation.ShortCode,
      is_paying: subscription.isSubscribed,
      plan_level: subscription?.plan?.name || subscription?.plan?.title || 'no plan',
      org_name: organisation.name || organisation.Name,
      is_in_trial: organisation.isInTrial,
      shortcode: organisation.ShortCode,
      expired_trial: organisation.expiredTrial,
      is_suspended: organisation.isSuspended,
      subscription_status: subscriptionStatus(
        subscription.isInTrial,
        subscription.isSubscribed,
        subscription.isSuspended,
        subscription.isCancelled,
      ),
      subscription_service: subscription.subscriptionService,
      integration: organisation.accountingSoftware,
      org_created_at: organisation.createdAt,
      connection_status: organisation.connectionStatus,
      country_code: organisation.CountryCode,
      email_integration: nylasConnected,
      uses_monthly_statement: isMonthlyStatement,
      uses_sms_templates: isSMSUser,
      call_reminders_in_use:
        schedules &&
        schedules.length > 0 &&
        schedules.some(s => Array.isArray(s.reminders) && s.reminders.length > 0),
      total_schedules: schedules?.length,
    },
  });
};

let TRACKERS = [
  {
    name: 'hubspot',
    env: [Environments.ENV_VARS],
    initialised: false,
    mustBeLoggedIn: false,
    mustBeConnected: false,
    init: initHubspot,
    configure: configureHubspot,
  },
  {
    // must be configured after hubspot tracker
    name: 'pendo',
    env: [Environments.ENV_VARS],
    mustBeLoggedIn: true,
    mustBeConnected: true,
    initialised: false,
    init: initPendo,
    configure: configurePendo,
  },
];

function initTracking() {
  TRACKERS = TRACKERS.map(tracking => {
    try {
      if (!tracking.env.includes(import.meta.env.VITE_ENV)) {
        return tracking;
      }

      tracking.init();

      return {
        ...tracking,
        initialised: true,
      };
    } catch (e) {
      console.error(e);
      console.error(`${tracking.name} tracking failed to initialise`);
      return tracking;
    }
  });
}

function checkTrackingAllowed(tracking, isLoggedIn, ShortCode) {
  if (!tracking.env.includes(import.meta.env.VITE_ENV)) {
    console.info(`skipping config: ${tracking.name} on ${import.meta.env.VITE_ENV} env`);
    return false;
  }

  if (tracking.mustBeLoggedIn && !isLoggedIn) {
    console.info(`skipping config: ${tracking.name} requires user to be logged in`);
    return false;
  }

  if (tracking.mustBeConnected && !ShortCode) {
    console.info(`skipping config: ${tracking.name} requires organisation shortcode`);
    return;
  }

  if (!tracking.initialised) {
    console.error(`configuration error: ${tracking.name} tracking is not initialised`);
    return false;
  }

  return true;
}

function useConfigureTracking({ organisation, user }) {
  const { subscription, ShortCode } = organisation;
  const { isLoggedIn } = user;

  useEffect(() => {
    TRACKERS.forEach(tracking => {
      if (!checkTrackingAllowed(tracking, isLoggedIn, ShortCode)) {
        return;
      }

      try {
        tracking.configure(user, organisation, organisation.subscription);
      } catch (e) {
        console.error(e);
        console.error(`${tracking.name} failed to configure`);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ShortCode, subscription, isLoggedIn]);
}

export { useConfigureTracking, initTracking };
